import React from 'react'
import { Link } from '@fs/zion-router'
import { MoreLess, Separator, Paragraph, Row } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import GlossaryTooltip from '../glossaryTooltip/GlossaryTooltip'

const EMPTY_FIELD = '-'
const noMarginBottom = css`
  margin-bottom: 0px;
`
const paddingRight = css`
  margin-inline-end: 24px;
`

/**
 * A list item component that has a copy-to-clipboard option
 *
 * @param overline : String Title of the list item
 * @param items : String[] text line items. Items are prioritized over hyperlinks.
 * @param hyperlinks : Object[] expects objects like: {skipEncode: Boolean, text : String, to: String}
 * @param lineClamp : Number lines to show in MoreLess wrapper
 * @param hasMoreLess : boolean if the text is displayed in a MoreLess
 * @param rightElement : JSX.Element the IconButton used to copy to clipboard
 * @param glossaryDescription : String text description that is displayed in the tooltip
 * @return {JSX.Element}
 */
export default function ListItemGroupData({
  overline = '',
  items = [],
  hyperlinks = [],
  hasMoreLess = true,
  rightElement = null,
  glossaryDescription = '',
  commaSeparated = false,
  primaryText = null,
}) {
  const formattedText = getFormattedText(Array.isArray(items) ? items : [items], commaSeparated)
  const formattedHyperlinks = getFormattedHyperlinks(hyperlinks)

  if (!primaryText) {
    primaryText = EMPTY_FIELD
    if (formattedText)
      primaryText = hasMoreLess ? <MoreLess collapsedLineCount={3}>{formattedText}</MoreLess> : <>{formattedText}</>
    else if (formattedHyperlinks) primaryText = formattedHyperlinks
  }

  return (
    <>
      <Separator size="nano" />
      <div css={noMarginBottom}>
        <Paragraph size="xs" dense secondary>
          <GlossaryTooltip overline={overline} description={glossaryDescription} />
        </Paragraph>
      </div>
      <Row>
        <Paragraph size="sm" dense growX>
          {primaryText}
        </Paragraph>
        {rightElement && <div css={paddingRight}>{rightElement}</div>}
      </Row>
      <Separator size="xxs" />
    </>
  )
}

function getFormattedHyperlinks(hyperlinkItems = []) {
  const filteredItems = hyperlinkItems?.filter(Boolean)
  if (filteredItems?.length < 1) return null

  return (
    <>
      {filteredItems.map((item) => (
        <div key={item.text + item.to}>
          <Link target="_blank" external to={item.skipEncode ? item.to : encodeURI(item.to.replace('&', '%26'))}>
            {item.text}
          </Link>
        </div>
      ))}
    </>
  )
}

function getFormattedText(textItems, commaSeparated) {
  const formatter = new Intl.ListFormat(i18n.language, { style: 'short', type: 'unit' })
  const filteredItems = textItems?.filter((i) => !!i) ?? []
  if (filteredItems.length === 0) return null
  if (commaSeparated) return <>{formatter.format(filteredItems)}</>

  return filteredItems.map((val) => <div key={val}>{val}</div>)
}
